import { useState } from 'react';
import './printSetupSettings.css';
import { downloadSetupScript, resetPrintSetup, showPrintSetupAgain } from './printSetup';
import { ChromeTooOld, PrintSetupSteps } from './PrintSetupSteps';
import { DownloadIcon, PrintIcon, RefreshIcon } from './UltimateIcons';
import { usePrintSetup } from './usePrintSetup';

interface PrintSetupSettingsProps {
  now?: () => Date;
}

/**
 * The Label printer part of a Fulfillment settings dialog (SSD-7389): whether this Chrome was set up, the setup file
 * and its steps, and a way back to the reminder after "Don't show today" or for a PC whose setting has gone. Clearing
 * the mark touches only this browser's storage, never the Windows setting.
 */
export const PrintSetupSettings = ({ now = () => new Date() }: PrintSetupSettingsProps) => {
  const setup = usePrintSetup(now);
  const [stepsOpen, setStepsOpen] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  return (
    <section className="ff-print-settings" aria-labelledby="ff-print-settings-title">
      <div className="fps-head">
        <PrintIcon />
        <b id="ff-print-settings-title">Label printer</b>
        <span className={`fps-status ${setup.done ? 'fps-ok' : 'fps-todo'}`}>
          {setup.done ? 'Set up on this PC' : 'Not set up on this PC'}
        </span>
      </div>
      <p className="fps-note">
        Print sends a label straight to the label printer, with no print dialog, only on a PC whose Chrome has the print
        setting. The setup file adds it once per PC.
      </p>
      <ChromeTooOld />
      {setup.done ? (
        <div className="fps-state">
          <span>
            The setup file finished on this Chrome on {setup.doneOn}, so the yellow reminder does not show here.
            Clearing the mark only brings the reminder back; the Windows setting stays.
          </span>
          <button type="button" className="btn btn-line btn-sm" onClick={resetPrintSetup}>
            <RefreshIcon />
            Clear the setup mark
          </button>
        </div>
      ) : setup.hiddenToday ? (
        <div className="fps-state">
          <span>The yellow reminder is hidden for today on this Chrome.</span>
          <button type="button" className="btn btn-line btn-sm" onClick={showPrintSetupAgain}>
            <RefreshIcon />
            Show the reminder again
          </button>
        </div>
      ) : (
        <div className="fps-state">
          <span>The yellow reminder shows above every Fulfillment screen until this PC is set up.</span>
        </div>
      )}
      <div className="fps-actions">
        <button
          type="button"
          className="btn btn-blue btn-sm"
          onClick={() => {
            downloadSetupScript();
            setDownloaded(true);
            setStepsOpen(true);
          }}
        >
          <DownloadIcon />
          {downloaded ? 'Download again' : 'Download setup file (Windows)'}
        </button>
        <button
          type="button"
          className="btn btn-line btn-sm"
          aria-expanded={stepsOpen}
          onClick={() => setStepsOpen((was) => !was)}
        >
          {stepsOpen ? 'Hide steps' : 'Setup steps'}
        </button>
      </div>
      {stepsOpen && <PrintSetupSteps />}
    </section>
  );
};

export default PrintSetupSettings;
